import { useId, useMemo } from 'react';
import { RadioGroup } from '@headlessui/react';
import './ColorTagPickerModern.css';

/**
 * @typedef {object} ColorTagOption
 * @property {string} value
 * @property {string} [description]
 * @property {string} [name]
 * @property {string} clase
 * @property {string} [selectedClass]
 */

/**
 * @typedef {object} ColorTagPickerModernProps
 * @property {ColorTagOption[]} colors
 * @property {string} [value]
 * @property {(color: { _id: string, clase: string, name?: string }) => void} onChange
 * @property {string} [label]
 * @property {string} [hint]
 * @property {boolean} [disabled]
 * @property {string} [className]
 * @property {string} [id]
 * @property {string} [error]
 * @property {boolean} [required]
 */

const getColorLabel = (color) => color?.description || color?.name || 'Color';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

/**
 * @param {ColorTagPickerModernProps} props
 */
export const ColorTagPickerModern = ({
    colors = [],
    value = '',
    onChange,
    label = 'Etiqueta de color',
    hint,
    disabled = false,
    className = '',
    id,
    error = null,
    required = false,
}) => {
    const generatedId = useId();
    const fieldId = id || `color-tag-picker-${generatedId}`;

    const normalizedColors = useMemo(
        () => (Array.isArray(colors) ? colors : []).filter((item) => item?.value && item?.clase),
        [colors],
    );

    const handleChange = (nextValue) => {
        if (disabled) return;
        const color = normalizedColors.find((item) => String(item.value) === String(nextValue));
        if (!color) return;
        onChange({
            _id: color.value,
            clase: color.clase,
            name: color.name || color.description,
        });
    };

    return (
        <div className={`color-tag-picker ${className}`.trim()}>
            {label ? (
                <label id={`${fieldId}-label`} className="color-tag-picker__label">
                    {label}
                    {required ? (
                        <span className="color-tag-picker__required" aria-hidden="true">*</span>
                    ) : null}
                </label>
            ) : null}

            <div
                className="color-tag-picker__field"
                data-disabled={disabled}
                data-error={error ? 'true' : 'false'}
            >
                {normalizedColors.length > 0 ? (
                    <RadioGroup
                        value={value || undefined}
                        onChange={handleChange}
                        disabled={disabled}
                        aria-labelledby={label ? `${fieldId}-label` : undefined}
                    >
                        <RadioGroup.Label className="sr-only">
                            {label || 'Seleccionar etiqueta de color'}
                        </RadioGroup.Label>
                        <div className="color-tag-picker__swatches">
                            {normalizedColors.map((color) => {
                                const colorLabel = getColorLabel(color);
                                return (
                                    <RadioGroup.Option
                                        key={color.value}
                                        value={String(color.value)}
                                        className={({ active, checked }) =>
                                            classNames(
                                                color.selectedClass,
                                                active && checked ? 'ring ring-offset-1' : '',
                                                !active && checked ? 'ring-2' : '',
                                                'color-tag-picker__option',
                                            )
                                        }
                                    >
                                        <RadioGroup.Label as="span" className="sr-only">
                                            {colorLabel}
                                        </RadioGroup.Label>
                                        <span
                                            aria-hidden="true"
                                            className={classNames(
                                                color.clase,
                                                'color-tag-picker__swatch',
                                            )}
                                        />
                                    </RadioGroup.Option>
                                );
                            })}
                        </div>
                    </RadioGroup>
                ) : (
                    <p className="color-tag-picker__empty" role="status">
                        No hay colores disponibles
                    </p>
                )}
            </div>

            {hint ? (
                <p className="color-tag-picker__hint">{hint}</p>
            ) : null}

            {error ? (
                <p className="color-tag-picker__error" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};
